/**
 * Unified Loading State Components
 * Provides consistent loading UX across the application
 */
import { cn } from '@/lib/utils';
import { Loader2, LucideIcon } from 'lucide-react';
import { Card, CardContent } from './card';
import { Skeleton } from './skeleton';

interface LoadingStateProps {
  message?: string;
  className?: string;
}

export function LoadingSpinnerState({ message = 'Loading...', className }: LoadingStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center py-12 gap-3", className)}>
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground animate-pulse">{message}</p>
    </div>
  );
}

export function LoadingCardState({ message, className }: LoadingStateProps) {
  return (
    <Card className={cn("border-dashed", className)}>
      <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
      </CardContent>
    </Card>
  );
}

interface TableLoadingProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function TableLoadingState({ rows = 5, columns = 4, className }: TableLoadingProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton 
              key={j} 
              className={cn(
                "h-10",
                j === 0 ? "w-12" : "flex-1"
              )} 
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface CardGridLoadingProps {
  cards?: number;
  className?: string;
}

export function CardGridLoadingState({ cards = 4, className }: CardGridLoadingProps) {
  return (
    <div className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-4", className)}>
      {Array.from({ length: cards }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-full mb-4" />
            <Skeleton className="h-3 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface ListLoadingProps {
  items?: number;
  className?: string;
}

export function ListLoadingState({ items = 5, className }: ListLoadingProps) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface InlineLoadingProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function InlineLoading({ size = 'md', className }: InlineLoadingProps) {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <Loader2 className={cn("animate-spin text-muted-foreground", sizeClasses[size], className)} />
  );
}

interface PageLoadingProps {
  title?: string;
  className?: string;
}

export function PageLoadingState({ title, className }: PageLoadingProps) {
  return (
    <div className={cn("space-y-6", className)}>
      {title && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>
      )}
      <CardGridLoadingState cards={4} />
      <Card>
        <CardContent className="p-6">
          <TableLoadingState rows={5} columns={5} />
        </CardContent>
      </Card>
    </div>
  );
}
